export class RingBuffer {
    constructor(capacity) {
        this._capacity = capacity;
        this._buffer = new Array(capacity);
        this._head = 0;
        this._size = 0;
    }
    push(item) {
        this._buffer[this._head] = item;
        this._head = (this._head + 1) % this._capacity;
        if (this._size < this._capacity) {
            this._size++;
        }
    }
    toArray() {
        const result = [];
        for (let i = 0; i < this._size; i++) {
            const idx = (this._head - 1 - i + this._capacity) % this._capacity;
            result.push(this._buffer[idx]);
        }
        return result;
    }
    clear() {
        this._buffer = new Array(this._capacity);
        this._head = 0;
        this._size = 0;
    }
    get size() {
        return this._size;
    }
}
const ALL_INCREMENTAL_OP_TYPES = ['getEnterpriseUnitContacts', 'getEnterpriseContactDetail', 'getEnterpriseContactBase', 'searchEnterpriseContacts'];
function generateSessionId() {
    return Date.now() + '_' + Math.random().toString(36).slice(2, 8);
}
function buildTaskSummary(task, sessionStatus) {
    var _a;
    const summary = {
        time: (_a = task.endTime) !== null && _a !== void 0 ? _a : task.startTime,
        contactCount: task.serverContactCount,
        orgCount: task.serverOrgCount,
        status: sessionStatus,
    };
    if (task.domain !== undefined) {
        summary.domain = task.domain;
    }
    return summary;
}
function cloneSyncTask(task) {
    const clone = {
        type: task.type,
        phase: task.phase,
        serverContactCount: task.serverContactCount,
        serverOrgCount: task.serverOrgCount,
        dexieProgress: {},
        lokiProgress: {},
        startTime: task.startTime,
    };
    if (task.domain !== undefined)
        clone.domain = task.domain;
    if (task.enterpriseId !== undefined)
        clone.enterpriseId = task.enterpriseId;
    if (task.duration !== undefined)
        clone.duration = task.duration;
    if (task.error !== undefined)
        clone.error = task.error;
    if (task.endTime !== undefined)
        clone.endTime = task.endTime;
    for (const key of Object.keys(task.dexieProgress)) {
        const p = task.dexieProgress[key];
        clone.dexieProgress[key] = { total: p.total, written: p.written, status: p.status };
    }
    for (const key of Object.keys(task.lokiProgress)) {
        const p = task.lokiProgress[key];
        clone.lokiProgress[key] = { total: p.total, written: p.written, status: p.status };
    }
    return clone;
}
function cloneSession(session) {
    const clone = {
        id: session.id,
        startTime: session.startTime,
        status: session.status,
        trigger: session.trigger,
        account: session.account,
        tasks: session.tasks.map(cloneSyncTask),
    };
    if (session.endTime !== undefined)
        clone.endTime = session.endTime;
    return clone;
}
function cloneSummary(summary) {
    return {
        personal: summary.personal ? Object.assign({}, summary.personal) : null,
        personalOrg: summary.personalOrg ? Object.assign({}, summary.personalOrg) : null,
        enterprise: summary.enterprise ? Object.assign({}, summary.enterprise) : null,
        total: summary.total,
        syncDuration: summary.syncDuration,
    };
}
export class ContactSyncStatusTracker {
    constructor() {
        this.currentSession = null;
        this.lastSyncSummary = null;
        this._timerSyncCount = 0;
        this.incrementalBuffers = {
            getEnterpriseUnitContacts: new RingBuffer(50),
            getEnterpriseContactDetail: new RingBuffer(50),
            getEnterpriseContactBase: new RingBuffer(50),
            searchEnterpriseContacts: new RingBuffer(50),
        };
        this.syncHistoryBuffer = new RingBuffer(20);
    }
    startSession(trigger, account) {
        if (this.currentSession !== null && this.currentSession.account === account) {
            this.endSession(this.currentSession.id, 'error');
        }
        if (trigger === 'timer') {
            this._timerSyncCount++;
        }
        else if (trigger === 'force') {
            this._timerSyncCount = 0;
        }
        const id = generateSessionId();
        this.currentSession = {
            id,
            startTime: Date.now(),
            status: 'running',
            trigger,
            account,
            tasks: [],
        };
        return id;
    }
    endSession(sessionId, status) {
        if (this.currentSession === null || this.currentSession.id !== sessionId) {
            return;
        }
        const session = this.currentSession;
        session.endTime = Date.now();
        session.status = status;
        let personalSummary = null;
        let personalOrgSummary = null;
        let enterpriseSummary = null;
        let total = 0;
        let personalCount = 0;
        let personalOrgCount = 0;
        let enterpriseContactCount = 0;
        let enterpriseOrgCount = 0;
        for (const task of session.tasks) {
            total += task.serverContactCount + task.serverOrgCount;
            switch (task.type) {
                case 'personal':
                    personalSummary = buildTaskSummary(task, session.status);
                    personalCount = task.serverContactCount;
                    break;
                case 'personalOrg':
                    personalOrgSummary = buildTaskSummary(task, session.status);
                    personalOrgCount = task.serverContactCount;
                    break;
                case 'enterprise':
                    enterpriseSummary = buildTaskSummary(task, session.status);
                    enterpriseContactCount = task.serverContactCount;
                    enterpriseOrgCount = task.serverOrgCount;
                    break;
            }
        }
        this.lastSyncSummary = {
            personal: personalSummary,
            personalOrg: personalOrgSummary,
            enterprise: enterpriseSummary,
            total,
            syncDuration: session.endTime - session.startTime,
        };
        this.syncHistoryBuffer.push({
            trigger: session.trigger,
            account: session.account,
            startTime: session.startTime,
            endTime: session.endTime,
            status: session.status,
            syncDuration: session.endTime - session.startTime,
            personalCount,
            personalOrgCount,
            enterpriseContactCount,
            enterpriseOrgCount,
        });
        this.currentSession = null;
    }
    getCurrentSession() {
        return this.currentSession;
    }
    recordTask(sessionId, init) {
        var _a;
        if (this.currentSession === null || this.currentSession.id !== sessionId) {
            return;
        }
        const task = {
            type: init.type,
            phase: (_a = init.phase) !== null && _a !== void 0 ? _a : 'fetch',
            serverContactCount: 0,
            serverOrgCount: 0,
            dexieProgress: {},
            lokiProgress: {},
            startTime: Date.now(),
        };
        if (init.domain !== undefined)
            task.domain = init.domain;
        if (init.enterpriseId !== undefined)
            task.enterpriseId = init.enterpriseId;
        this.currentSession.tasks.push(task);
    }
    updateTaskProgress(sessionId, taskType, progress, domain) {
        const task = this.findTask(sessionId, taskType, domain);
        if (task === null)
            return;
        if (progress.phase !== undefined)
            task.phase = progress.phase;
        if (progress.serverContactCount !== undefined)
            task.serverContactCount = progress.serverContactCount;
        if (progress.serverOrgCount !== undefined)
            task.serverOrgCount = progress.serverOrgCount;
        if (progress.error !== undefined)
            task.error = progress.error;
        if (task.phase === 'done' || task.phase === 'error') {
            task.endTime = Date.now();
            task.duration = task.endTime - task.startTime;
        }
    }
    updateDexieProgress(sessionId, taskType, tableName, progress, domain) {
        var _a, _b, _c;
        const task = this.findTask(sessionId, taskType, domain);
        if (task === null)
            return;
        const existing = task.dexieProgress[tableName];
        if (existing) {
            if (progress.total !== undefined)
                existing.total = progress.total;
            if (progress.written !== undefined)
                existing.written = progress.written;
            if (progress.status !== undefined)
                existing.status = progress.status;
        }
        else {
            task.dexieProgress[tableName] = {
                total: (_a = progress.total) !== null && _a !== void 0 ? _a : 0,
                written: (_b = progress.written) !== null && _b !== void 0 ? _b : 0,
                status: (_c = progress.status) !== null && _c !== void 0 ? _c : 'pending',
            };
        }
    }
    updateLokiProgress(sessionId, taskType, collectionName, progress, domain) {
        var _a, _b, _c;
        const task = this.findTask(sessionId, taskType, domain);
        if (task === null)
            return;
        const existing = task.lokiProgress[collectionName];
        if (existing) {
            if (progress.total !== undefined)
                existing.total = progress.total;
            if (progress.written !== undefined)
                existing.written = progress.written;
            if (progress.status !== undefined)
                existing.status = progress.status;
        }
        else {
            task.lokiProgress[collectionName] = {
                total: (_a = progress.total) !== null && _a !== void 0 ? _a : 0,
                written: (_b = progress.written) !== null && _b !== void 0 ? _b : 0,
                status: (_c = progress.status) !== null && _c !== void 0 ? _c : 'pending',
            };
        }
    }
    recordIncrementalOp(op) {
        this.incrementalBuffers[op.api].push(op);
    }
    getSnapshot() {
        const incrementalOps = {};
        for (const opType of ALL_INCREMENTAL_OP_TYPES) {
            incrementalOps[opType] = this.incrementalBuffers[opType].toArray().map(op => (Object.assign({ api: op.api, timestamp: op.timestamp, returnCount: op.returnCount, targetTables: [...op.targetTables], dexieWritten: op.dexieWritten, lokiWritten: op.lokiWritten }, (op.params !== undefined ? { params: op.params } : {}))));
        }
        return {
            currentSession: this.currentSession !== null ? cloneSession(this.currentSession) : null,
            lastSyncSummary: this.lastSyncSummary !== null ? cloneSummary(this.lastSyncSummary) : null,
            incrementalOps,
            syncHistory: this.syncHistoryBuffer.toArray(),
            timerSyncCount: this._timerSyncCount,
        };
    }
    findTask(sessionId, taskType, domain) {
        var _a, _b;
        if (this.currentSession === null || this.currentSession.id !== sessionId) {
            return null;
        }
        if (domain !== undefined && domain !== '') {
            return (_a = this.currentSession.tasks.find(t => t.type === taskType && t.domain === domain)) !== null && _a !== void 0 ? _a : null;
        }
        return (_b = this.currentSession.tasks.find(t => t.type === taskType)) !== null && _b !== void 0 ? _b : null;
    }
}
